import { InputBlock } from '@coast/schemas/index';
import { ReactElement, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { FormContext, useCardFormContext } from '@shared/components/blocks/contexts/form';
import { ColorThemeContext, getAnchorName, grid } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { getTextPropsFromTextStyle } from '../utils';
import { MBCardInputBlockBase } from './input/base';

interface Props<T> {
  block: InputBlock;
  stateless?: boolean;
  renderPlaceholder?(): ReactNode;
  renderEditForm(args: { value: T; setValue(args: T): void; onSave(): void }): ReactElement;
  renderInput(args: {
    value: T;
    setValue(args: T): void;
    onBlur(): void;
    mode: FormContext['mode'];
  });
  onReset?(): void;
}

/**
 * Wraps {@link MBCardInputBlockBase} and adds bottom sheet functionality.
 *
 */
export function AnchoredSheetInputBlock<T extends string | number>({
  block,
  stateless,
  renderEditForm,
  renderPlaceholder,
  renderInput,
  onReset,
}: Props<T>) {
  const anchorName = getAnchorName('sheet-input-block', block.name);
  const colors = useContext(ColorThemeContext);
  const { setFieldValue, handleBlur, values, mode } = useCardFormContext();
  const [sheetVisible, setSheetVisible] = useState(false);

  const value = values[block.name];
  const [innerValue, setInnerValue] = useState(value);
  const onBlur = useMemo(() => handleBlur(block.name), [handleBlur, block.name]);
  const onChange = useCallback(
    (updatedValue: T) => setFieldValue(block.name, updatedValue),
    [setFieldValue, block.name],
  );

  const hideSheet = useCallback(() => setSheetVisible(false), []);

  const hideSheetAndSave = useCallback(() => {
    if (!stateless) {
      setInnerValue(innerValue);
      onChange(innerValue);
    }
    setSheetVisible(false);
  }, [stateless, innerValue, onChange]);

  const showSheet = useCallback(() => setSheetVisible(true), []);

  const reset = useCallback(() => {
    hideSheet();
    onReset?.();
    if (!stateless) {
      setInnerValue(value);
    }
  }, [hideSheet, stateless, value, onReset]);

  const isDesktop = useIsDesktop();

  /**
   * We only use the bottom sheet on:
   *
   * 1) Mobile (Web and Native)
   * 2) For forms in Edit mode
   * 3) Except for the 'name' block, which is always in autofocus
   *
   */
  const shouldUseBottomSheet = !isDesktop && mode === 'EDIT' && !block.autoFocus;

  const textProps = getTextPropsFromTextStyle(block.textStyle);

  const editForm = useMemo(
    () => renderEditForm({ value: innerValue, setValue: setInnerValue, onSave: hideSheetAndSave }),
    [hideSheetAndSave, innerValue, renderEditForm],
  );

  const input = useMemo(
    () => renderInput({ value, setValue: onChange, onBlur, mode }),
    [mode, onBlur, onChange, renderInput, value],
  );

  const placeholder = useMemo(() => {
    const defaultValue = value ?? block.placeholder;
    const renderedPlacedholder = renderPlaceholder ? renderPlaceholder() : defaultValue;
    return renderedPlacedholder || defaultValue;
  }, [renderPlaceholder, value, block]);

  return (
    <MBCardInputBlockBase block={block} onPress={shouldUseBottomSheet ? showSheet : undefined}>
      {shouldUseBottomSheet ? (
        <View style={{ anchorName }}>
          <Text {...textProps} inactive={!value} onClick={showSheet}>
            {placeholder}
          </Text>
        </View>
      ) : (
        input
      )}
      {sheetVisible && (
        <AnchoredSheet
          anchorName={anchorName}
          initialSize="half"
          onClose={reset}
          renderTitleBar={() => (
            <AnchoredSheetTitleBar
              onRightIconPress={reset}
              title={block.label}
              rightIcon="exit"
              rightIconColor={colors.foregroundActive}
              rightIconSize={grid(4)}
            />
          )}
        >
          {editForm}
        </AnchoredSheet>
      )}
    </MBCardInputBlockBase>
  );
}
